import React from "react";
import axios from "../../../helpers/axios";

/*
 * Шрифты надписи на камне — те же файлы, что лежат на бэкенде в files/fonts
 * (сайт берёт их через API /office/:officeId/document-fonts/:key). PDF рисует ими же.
 * [ключ, название, CSS-семейство, обычный файл, жирный файл]
 */
export const STONE_FONTS = [
    ["roboto", "Roboto (обычный)", "BlankRoboto", "Roboto-Regular.ttf", "Roboto-Bold.ttf"],
    ["montserrat", "Montserrat (строгий)", "BlankMontserrat", "Montserrat-Medium.ttf", "Montserrat-Bold.ttf"],
    ["ptserif", "PT Serif (с засечками)", "BlankPTSerif", "PT_Serif-Web-Regular.ttf", "PT_Serif-Web-Bold.ttf"],
    ["cormorant", "Cormorant Garamond (классика)", "BlankCormorant", "CormorantGaramond-SemiBold.ttf", "CormorantGaramond-Bold.ttf"],
    ["marck", "Marck Script (рукописный)", "BlankMarck", "MarckScript-Regular.ttf", "MarckScript-Regular.ttf"],
];

/** Ширина букв относительно Roboto — как в PDF (подбор размера, чтобы строка влезла). */
/*
 * Собственная высота шрифта (ascent − descent) / unitsPerEm — как её видит PDF.
 * PDF рисует строку высотой 1,15 размера и ставит верх букв на верх строки,
 * браузер — по центру строки; разницу сдвигаем (lineStyle).
 */
export const STONE_FONT_RATIO = { roboto: 1.1719, montserrat: 1.219, ptserif: 1.325, cormorant: 1.211, marck: 1.249 };

/** Стиль строки надписи — чтобы на листе стояло там же, где в PDF. */
export const lineStyle = (key, size, extra = {}, mult = 1.15) => ({
    fontSize: size,
    lineHeight: mult,
    position: "relative",
    top: (((STONE_FONT_RATIO[key] || 1.17) - mult) / 2) * size,
    ...extra,
});

/** Размеры и интервалы надписи (пункты; как на листе при 100%) — те же умолчания, что в PDF. */
const num = (value, fallback) => (value !== "" && value !== null && value !== undefined && Number.isFinite(Number(value)) ? Number(value) : fallback);
export const typoOf = (front = {}) => ({
    surname: num(front.surnameSize, 15),
    name: num(front.nameSize, 11),
    dates: num(front.datesSize, 9.5),
    fioLine: num(front.fioLine, 1.15),
    fioLetter: num(front.fioLetter, 0),
    datesLine: num(front.datesLine, 1.15),
    datesLetter: num(front.datesLetter, 0),
    epitaph: num(front.epitaphSize, 9),
    epitaphLine: num(front.epitaphLine, 1.15),
    epitaphLetter: num(front.epitaphLetter, 0),
});

/** Знаков в эпитафии (для цены): всего и без пробелов — так же считает PDF. */
export const epitaphChars = (text) => {
    const value = String(text || "").replace(/\r/g, "");
    return { total: value.replace(/\n/g, "").length, letters: value.replace(/\s/g, "").length };
};

/** Строка с межбуквенным: в PDF интервал только между буквами — убираем хвост после последней. */
export const SpacedLine = ({ text, letter, style }) => (
    <div style={style}>
        {/* Шрифт задаём и самому тексту: общие стили сайта могут переопределять шрифт у span. */}
        <span
            style={{
                fontFamily: style.fontFamily,
                fontWeight: style.fontWeight,
                fontStyle: style.fontStyle,
                color: style.color,
                letterSpacing: letter,
                marginRight: letter ? -letter : 0,
            }}
        >
            {text}
        </span>
    </div>
);

/** Курсив есть только у Roboto; остальные шрифты в PDF — прямые. */
export const italicOf = (key) => (key === "roboto" || !key ? "italic" : "normal");

export const STONE_FONT_WIDTH = { roboto: 1, montserrat: 1.12, ptserif: 1.02, cormorant: 0.86, marck: 0.92 };

const loaded = new Map();

/*
 * Подгрузить шрифт в браузер (один раз) и вернуть CSS-семейство.
 * Файл берём через API (axios с токеном, как остальные запросы офиса), а не
 * по /public: со статики шрифт другого адреса браузер может не загрузить (CORS).
 */
export const ensureStoneFont = (officeId, key, onReady) => {
    const entry = STONE_FONTS.find((item) => item[0] === key) || STONE_FONTS[0];
    const family = entry[2];
    if (typeof document === "undefined" || typeof FontFace === "undefined") return family;
    if (!loaded.has(family)) {
        const load = async (weight) => {
            // Офис — через маршрут офиса; бланк до заказа на сайте — через «Печать формы».
            const url = officeId
                ? `/office/${encodeURIComponent(officeId)}/document-fonts/${encodeURIComponent(entry[0])}`
                : `/print-form/fonts/${encodeURIComponent(entry[0])}`;
            const response = await axios.get(url, {
                params: weight === "700" ? { weight: "bold" } : {},
                responseType: "arraybuffer",
            });
            const face = new FontFace(family, response.data, { weight });
            await face.load();
            document.fonts.add(face);
        };
        // Не загрузился — запоминаем и не дёргаем сервер снова (останется Roboto).
        loaded.set(family, Promise.all([load("400"), load("700")]).catch((error) => {
            console.warn("[blank font]", key, error?.message || error);
        }));
    }
    if (onReady) loaded.get(family)?.then(onReady);
    return family;
};
